/**
 * The Validate engine: everything wrong with a schema, found without asking a
 * model.
 *
 * The builder happily accepts a schema that cannot produce a usable name — a
 * value with no code, two values sharing one code, a code containing the very
 * delimiter that separates fields, a rule pointing at a column someone deleted
 * last week. None of that surfaces until a name comes out wrong, by which time
 * it is in a DAM and a reporting join.
 *
 * DETERMINISTIC ON PURPOSE. Every check here is arithmetic or a lookup, so it
 * runs in the browser against the draft ON SCREEN — including edits that have
 * not been autosaved yet — with no request, no cost and no waiting. A later
 * pass can add the judgement calls a model is actually good at (typos,
 * inconsistent casing); those belong beside these results, not instead of them.
 * The shortener already demonstrated what happens when a model is handed
 * arithmetic it cannot do.
 *
 * Pure function, no React: the same input always yields the same problems in
 * the same order, which is what makes it testable and what will let it run over
 * submitted asset rows later without a rewrite.
 */
import {
  canBindList,
  DEFAULT_DATE_MASK,
  getRuleBranches,
  isCompilableFieldPattern,
  matchesFieldPattern,
  MAX_TESTABLE_VALUE_LENGTH,
  type TaxonomyField,
  type TaxonomyOutput,
  type TaxonomyRule,
  type TaxonomyRuleConditionGroup,
  type TaxonomySettings,
  type TrackingCodePart,
} from '@/entities/taxonomy';
import type { ResolvedList } from '@/entities/taxonomy-list';
import { i18n } from '@/shared/i18n';
import { isBlankValue } from './pruneEmptyRows';
import { channelStatuses } from './taxonomyString';
import {
  DEFAULT_FIELD_DELIMITER,
  DEFAULT_SUBFIELD_DELIMITER,
  isFieldInName,
  substituteResolvedValues,
} from '@inference.engine/taxonomy/web';

/**
 * Stable identifier for a kind of problem. Kept separate from the message so
 * the copy can be reworded without breaking anything that counts or groups by
 * type.
 */
export type SchemaProblemCode =
  | 'missing_code'
  | 'code_column_now_filled'
  | 'missing_value'
  | 'duplicate_code'
  | 'duplicate_value'
  | 'code_contains_delimiter'
  | 'code_contains_space'
  | 'value_breaks_pattern'
  | 'value_too_long'
  | 'pattern_never_matches'
  | 'field_has_no_values'
  | 'duplicate_field_name'
  | 'unknown_date_mask'
  | 'orphan_subfield'
  | 'broken_list_binding'
  | 'rule_dead_field'
  | 'rule_dead_value'
  | 'computed_dead_field'
  | 'computed_empty_pattern'
  | 'pattern_literal_contains_delimiter'
  | 'pattern_value_contains_delimiter'
  | 'pattern_unknown_date_mask'
  | 'tracking_dead_field'
  | 'linked_unconfigured'
  | 'output_dead_field'
  | 'over_channel_limit'
  | 'schema_has_no_fields';

export type SchemaProblemSeverity = 'error' | 'warning';

/**
 * Where a problem should be highlighted. A `cell` target paints one grid cell,
 * a `field` target paints the column header, and `schema` problems have no
 * anchor and are reported in the summary only.
 */
export type SchemaProblemTarget =
  | { kind: 'cell'; fieldId: string; row: number; column: 'value' | 'code' }
  | { kind: 'field'; fieldId: string }
  | { kind: 'schema' };

export interface SchemaProblem {
  /** Stable across runs for the same underlying problem; used as the React key. */
  id: string;
  code: SchemaProblemCode;
  severity: SchemaProblemSeverity;
  target: SchemaProblemTarget;
  /** What is wrong, in the user's terms. */
  message: string;
  /** What to do about it. Shown under the message in the tooltip. */
  hint?: string;
  /** A one-click repair the summary can offer. */
  fix?: SchemaProblemFix;
}

/** Sets the field's binding to emit each option's display value as its code. */
export interface SchemaProblemFix {
  kind: 'use_display_as_code';
  fieldId: string;
}

export interface ValidateSchemaInput {
  fields: TaxonomyField[];
  rules: TaxonomyRule[];
  settings: TaxonomySettings | undefined;
  outputs: TaxonomyOutput[] | undefined;
  /** Platforms the schema targets, for the character-budget check. */
  platforms: string[];
  /** Vocabularies resolved from shared lists, keyed by field id. */
  resolvedLists: Record<string, ResolvedList>;
}

export interface ValidateSchemaResult {
  problems: SchemaProblem[];
  errorCount: number;
  warningCount: number;
}

/** Date-mask tokens the pickers and the backend both understand. */
const DATE_TOKENS = /YYYY|YY|MMM|MM|DD/;

/** A field whose cells the user owns — bound and generated columns are not editable. */
function ownsItsValues(field: TaxonomyField): boolean {
  return !field.listRef?.listId && field.type !== 'computed' && field.type !== 'linked';
}

/** Whether the field's values are governed, so a referenced value must exist. */
function isGoverned(field: TaxonomyField): boolean {
  // The type half goes through `canBindList` rather than repeating
  // `=== 'dropdown'`: that predicate exists precisely because four writers each
  // kept their own copy of this test and drifted apart.
  return canBindList(field.type) || !!field.listRef?.listId;
}

/**
 * Two codes that read as one: case is ignored and whitespace runs collapse, so
 * `North America` and `north  america` are reported as one reused code.
 */
function duplicateKey(code: string): string {
  return code.replace(/\s+/g, ' ').toLowerCase();
}

/** Every condition in a rule's tree, flattened. Iterative — the tree can nest. */
function flattenConditions(group: TaxonomyRuleConditionGroup | undefined) {
  if (!group) return [];
  const out: { fieldId: string; value: string }[] = [];
  const queue = [group];
  for (let head = 0; head < queue.length; head += 1) {
    const node = queue[head];
    // `Array.isArray` rather than `?? []`: the type says both are present, but
    // rules are persisted as raw jsonb and the API deliberately accepts a group
    // with no `groups` key as a leaf, so a stored rule really can arrive
    // without them.
    if (Array.isArray(node.conditions)) {
      for (const c of node.conditions) out.push({ fieldId: c.fieldId, value: c.value });
    }
    if (Array.isArray(node.groups)) {
      for (const child of node.groups) queue.push(child);
    }
  }
  return out;
}

/**
 * Everything wrong with the schema, ordered errors-first so the summary and the
 * first highlight agree about what matters most.
 */
export function validateSchema(input: ValidateSchemaInput): ValidateSchemaResult {
  const { fields, rules, settings, outputs, platforms, resolvedLists } = input;
  const problems: SchemaProblem[] = [];
  const seenIds = new Set<string>();
  // Ids are React keys, so two problems can never share one. A single
  // branch can reference the same dead column twice (`X is US OR X is CA`),
  // which otherwise duplicates the React key, the sentence and the count.
  const add = (p: Omit<SchemaProblem, 'id'> & { id?: string }) => {
    const id = p.id ?? problemId(p.code, p.target);
    if (seenIds.has(id)) return;
    seenIds.add(id);
    problems.push({ ...p, id });
  };

  const fieldDelim = settings?.fieldDelimiter || DEFAULT_FIELD_DELIMITER;
  const subfieldDelim = settings?.subfieldDelimiter || DEFAULT_SUBFIELD_DELIMITER;
  const delimiters = dedupe([fieldDelim, subfieldDelim]).filter(Boolean);
  const byId = new Map(fields.map((f) => [f.id, f]));

  /**
   * The two problems any pattern of parts can carry, wherever that pattern
   * lives: a reference to a column that is gone, and a date mask made of tokens
   * nothing recognises.
   *
   * `renderDateMask` is a replace-chain over YYYY/MMM/MM/DD/YY, so an
   * unrecognised mask comes back UNCHANGED — a part masked "Q1" emits the
   * literal text "Q1" forever, looking like a date. The DTO does not catch it
   * either: `format` is only length-bounded.
   *
   * DELIBERATELY NOT HERE: the separator checks. They belong to a pattern whose
   * output is a NAME that gets parsed back apart. The schema-level tracking code
   * is a standalone join key that is never split, and the composer seeds a new
   * literal with the sub-field separator on purpose — see the caller below.
   *
   * No `break`: two different codes come out of one walk, so stopping at the
   * first would let whichever problem sits earliest in the pattern hide the
   * other. `add` already dedupes per code and target.
   */
  const checkPatternParts = (
    // NOT `readonly`: `Array.isArray` narrows a readonly array to `any[]`, which
    // silently drops the element type and every part access below with it.
    parts: TrackingCodePart[] | undefined,
    target: SchemaProblemTarget,
    idPrefix: string,
    // Outputs and computed columns already report a dead reference with a message
    // naming the surface the user has to open, so they take the mask check only.
    { deadField = true }: { deadField?: boolean } = {},
  ) => {
    // `parts` is jsonb, so the shape is only trustworthy after checking it.
    if (!Array.isArray(parts)) return;

    parts.forEach((part, index) => {
      if (deadField && part.kind === 'field' && part.fieldId && !byId.has(part.fieldId)) {
        add({
          // Explicit: `problemId` collapses a `schema` target to the bare code,
          // so without this a second dangling reference is silently swallowed.
          id: `${idPrefix}:dead_field:${part.fieldId}`,
          code: 'tracking_dead_field',
          severity: 'error',
          target,
          message: i18n.t('knowledge:taxonomy.validateSchema.patternReferencesColumnNoLonger'),
          hint: i18n.t('knowledge:taxonomy.validateSchema.editPatternDropReplaceMissing'),
        });
      }

      const mask = part.format?.trim();
      if (part.kind === 'date' && mask && !DATE_TOKENS.test(mask)) {
        add({
          id: `${idPrefix}:date_mask:${index}`,
          code: 'pattern_unknown_date_mask',
          // `warning`, matching `unknown_date_mask` on a date FIELD's own format:
          // same failure mode, and the schema still produces a name — just one
          // carrying the mask verbatim, which a user may even have meant.
          severity: 'warning',
          target,
          message: i18n.t('knowledge:taxonomy.validateSchema.noDateTokens', { format: mask }),
          hint: i18n.t('knowledge:taxonomy.validateSchema.useTokensLike', {
            mask: DEFAULT_DATE_MASK,
          }),
        });
      }
    });
  };

  if (fields.length === 0) {
    add({
      code: 'schema_has_no_fields',
      severity: 'error',
      target: { kind: 'schema' },
      message: i18n.t('knowledge:taxonomy.validateSchema.schemaHasNoFieldsYet'),
      hint: i18n.t('knowledge:taxonomy.validateSchema.addFieldGetStarted'),
    });
  }

  // ── Field names ─────────────────────────────────────────────────────────
  const nameSeen = new Map<string, string>();
  for (const field of fields) {
    const key = field.name.trim().toLowerCase();
    if (!key) continue;
    const first = nameSeen.get(key);
    if (first) {
      add({
        code: 'duplicate_field_name',
        severity: 'warning',
        target: { kind: 'field', fieldId: field.id },
        message: i18n.t('knowledge:taxonomy.validateSchema.anotherFieldCalled', {
          name: field.name,
        }),
        hint: i18n.t('knowledge:taxonomy.validateSchema.twoColumnsOneNameMake'),
      });
    } else {
      nameSeen.set(key, field.id);
    }
  }

  for (const field of fields) {
    // ── Structure ─────────────────────────────────────────────────────────
    if (field.parentId && !byId.has(field.parentId)) {
      add({
        code: 'orphan_subfield',
        severity: 'error',
        target: { kind: 'field', fieldId: field.id },
        message: i18n.t('knowledge:taxonomy.validateSchema.subfieldSParentColumnNo'),
        hint: i18n.t('knowledge:taxonomy.validateSchema.detachParentDelete'),
      });
    }

    const binding = field.listRef?.listId ? resolvedLists[field.id] : undefined;
    // `pending` is deliberately not an error: a list that has not arrived yet
    // would otherwise be reported as deleted, and the hint tells the user to
    // unlink — destroying a governed vocabulary over a cold cache.
    if (binding?.error && !binding.pending) {
      add({
        code: 'broken_list_binding',
        severity: 'error',
        target: { kind: 'field', fieldId: field.id },
        message: i18n.t('knowledge:taxonomy.validateSchema.listBindingBroken', {
          reason: binding.error.toLowerCase(),
        }),
        hint: i18n.t('knowledge:taxonomy.validateSchema.rePointBindingLiveList'),
      });
    }

    if (field.type === 'linked' && !field.link?.targetSchemaId) {
      add({
        code: 'linked_unconfigured',
        severity: 'warning',
        target: { kind: 'field', fieldId: field.id },
        message: i18n.t('knowledge:taxonomy.validateSchema.linkedColumnHasNoTarget'),
        hint: i18n.t('knowledge:taxonomy.validateSchema.pickSchemaPullsJoinKey'),
      });
    }

    if (field.type === 'computed') {
      const pattern = field.pattern ?? [];
      if (pattern.length === 0) {
        add({
          code: 'computed_empty_pattern',
          severity: 'error',
          target: { kind: 'field', fieldId: field.id },
          message: i18n.t('knowledge:taxonomy.validateSchema.variableColumnHasNoPattern'),
          hint: i18n.t('knowledge:taxonomy.validateSchema.openPatternComposerAddLeast'),
        });
      }
      /**
       * No `break` in this loop, deliberately. `add` dedupes on
       * `problemId(code, target)`, and every finding here targets the column, so
       * a second offending part of the SAME kind is already dropped — the break
       * that used to sit under the dead-field check bought nothing.
       *
       * It cost something, though: with three different codes reported from one
       * loop, breaking on the first means whichever problem appears earliest in
       * the pattern hides the others. A dead reference after a bad literal
       * simply would not be reported, and the user would fix one thing, re-run
       * Validate, and be handed another.
       */
      // Mask check only: the dead-reference report just below names this column.
      // eslint-disable-next-line i18next-templates/no-literal-string -- problem id prefix, not copy
      checkPatternParts(pattern, { kind: 'field', fieldId: field.id }, `computed:${field.id}`, {
        deadField: false,
      });

      for (const part of pattern) {
        if (part.kind === 'field' && part.fieldId && !byId.has(part.fieldId)) {
          add({
            code: 'computed_dead_field',
            severity: 'error',
            target: { kind: 'field', fieldId: field.id },
            message: i18n.t('knowledge:taxonomy.validateSchema.patternReferencesColumnNoLonger'),
            hint: i18n.t('knowledge:taxonomy.validateSchema.editPatternDropReplaceMissing'),
          });
        }

        /**
         * A computed column's parts land in the generated name, so a separator
         * inside one splits that name exactly as a separator inside a code
         * would. The two checks below are the pattern-side halves of
         * `code_contains_delimiter`.
         *
         * `settings.trackingCode.pattern` is deliberately NOT walked here even
         * though the same composer authors it: a tracking code is a standalone
         * join key that is never split back into fields, and the composer seeds
         * a new literal with the sub-field separator on purpose. Checking it
         * would report the composer's own default as an error.
         */
        if (part.kind === 'literal') {
          const text = part.text ?? '';
          const offending = delimiters.find((delim) => text.includes(delim));
          if (offending) {
            add({
              code: 'pattern_literal_contains_delimiter',
              severity: 'error',
              target: { kind: 'field', fieldId: field.id },
              message: i18n.t('knowledge:taxonomy.validateSchema.patternLiteralHasDelimiter', {
                text,
                delimiter: offending,
              }),
              hint: i18n.t('knowledge:taxonomy.validateSchema.editPatternRemoveSeparatorLiteral'),
            });
          }
        }

        // `source: 'value'` emits the referenced field's DISPLAY value, so a
        // clean code is no defence — the value is what reaches the name. A list's
        // display value lands as written, like its code, so only inline values
        // are checked. Keyed on `listRef` rather than a resolved binding, so a
        // list still loading does not flag the field's stale inline copy.
        if (part.kind === 'field' && part.source === 'value' && part.fieldId) {
          const ref = byId.get(part.fieldId);
          if (ref && !ref.listRef?.listId) {
            const hit = ref.values.find((v) => delimiters.some((delim) => v.value.includes(delim)));
            if (hit) {
              add({
                code: 'pattern_value_contains_delimiter',
                severity: 'error',
                target: { kind: 'field', fieldId: field.id },
                message: i18n.t('knowledge:taxonomy.validateSchema.patternValueHasDelimiter', {
                  field: ref.name,
                  value: hit.value,
                }),
                hint: i18n.t('knowledge:taxonomy.validateSchema.pullCodeInsteadRenameValue'),
              });
            }
          }
        }
      }
    }

    if (field.type === 'date' && field.format?.trim() && !DATE_TOKENS.test(field.format)) {
      add({
        code: 'unknown_date_mask',
        severity: 'warning',
        target: { kind: 'field', fieldId: field.id },
        message: i18n.t('knowledge:taxonomy.validateSchema.noDateTokens', { format: field.format }),
        hint: i18n.t('knowledge:taxonomy.validateSchema.useTokensLike', {
          mask: DEFAULT_DATE_MASK,
        }),
      });
    }

    const pattern = field.validationPattern?.trim();
    // The same predicate the API stores by, so this reports exactly what a save
    // would refuse rather than a second opinion about it.
    if (pattern && !isCompilableFieldPattern(pattern)) {
      add({
        code: 'pattern_never_matches',
        severity: 'warning',
        target: { kind: 'field', fieldId: field.id },
        message: i18n.t('knowledge:taxonomy.validateSchema.fieldSValidationPatternIsn'),
        hint: i18n.t('knowledge:taxonomy.validateSchema.fixPatternClearUnenforceableRule'),
      });
    }
    // ── Bound columns ─────────────────────────────────────────────────────
    // Their cells belong to the List, so findings anchor to the column. A list
    // code may hold spaces and either separator, since the name carries it as
    // written, so only duplicate and blank codes are checked.
    if (binding && !binding.error && !binding.pending) {
      const boundCodes = new Set<string>();

      /**
       * Offenders are COLLECTED, then reported once each with a count.
       *
       * Reporting per offender cannot work: `add` dedupes on `problemId(code,
       * target)` and all of these share a target, so only the first would ever
       * land — a loop whose body can fire once reads like it reports them all.
       * Giving each an explicit id would land them, and is worse: `errorCount`
       * drives the summary bar and the publish gate, so twenty blank codes would
       * read as twenty problems instead of one list to open.
       */
      const blankValues: string[] = [];
      // Keyed by CODE, so three options sharing one code are one reused code rather
      // than two sightings. Counting sightings told the user "reuses 2 codes" when
      // there was one, and no reading of the sentence made 2 the right number.
      const duplicateCodes = new Map<string, string>();

      // Read from `blankCodeValues`, NOT from a blank `row.code`: the resolver has
      // already replaced a blank code with the display value, which is the very
      // fallback this warns about. Testing `!row.code` here looked right and could
      // never fire.
      blankValues.push(...(binding.blankCodeValues ?? []));

      for (const row of binding.values) {
        const code = row.code.trim();
        if (!code) continue;

        const key = duplicateKey(code);
        if (boundCodes.has(key)) duplicateCodes.set(key, code);
        boundCodes.add(key);
      }

      if (blankValues.length) {
        // Offered only when every code is blank: with some codes present, emitting display
        // values would throw those codes away.
        const noCodesAtAll = blankValues.length >= binding.values.length;
        add({
          severity: 'error',
          target: { kind: 'field', fieldId: field.id },
          code: 'missing_code',
          message: i18n.t('knowledge:taxonomy.validateSchema.listValueHasNoCode', {
            list: binding.listName,
            count: blankValues.length,
            value: blankValues[0],
          }),
          hint: noCodesAtAll
            ? i18n.t('knowledge:taxonomy.validateSchema.listHasNoCodesUseDisplay')
            : i18n.t('knowledge:taxonomy.validateSchema.blankCodeCellListFalls'),
          ...(noCodesAtAll ? { fix: { kind: 'use_display_as_code', fieldId: field.id } } : {}),
        });
      }

      if (binding.unusedCodeColumnName !== undefined) {
        const column = binding.unusedCodeColumnName.trim();
        add({
          severity: 'warning',
          target: { kind: 'field', fieldId: field.id },
          code: 'code_column_now_filled',
          message: column
            ? i18n.t('knowledge:taxonomy.validateSchema.listColumnNowHasCodes', {
                list: binding.listName,
                column,
              })
            : i18n.t('knowledge:taxonomy.validateSchema.listNowHasCodes', {
                list: binding.listName,
              }),
          hint: column
            ? i18n.t('knowledge:taxonomy.validateSchema.listColumnNowHasCodesHint', { column })
            : i18n.t('knowledge:taxonomy.validateSchema.listNowHasCodesHint'),
        });
      }

      const duplicates = [...duplicateCodes.values()];
      if (duplicates.length) {
        add({
          severity: 'error',
          target: { kind: 'field', fieldId: field.id },
          code: 'duplicate_code',
          message: i18n.t('knowledge:taxonomy.validateSchema.listDuplicateCode', {
            list: binding.listName,
            count: duplicates.length,
            code: duplicates[0],
          }),
          hint: i18n.t('knowledge:taxonomy.validateSchema.twoValuesSharingCodeMake'),
        });
      }
    }

    // ── Cells ─────────────────────────────────────────────────────────────
    // Only for fields whose cells the user actually owns. A bound column's
    // values belong to its List, and a generated one has no cells at all.
    if (!ownsItsValues(field)) continue;

    const rows = field.values;
    const nonEmpty = rows.filter((v) => !isBlankValue(v));
    if (nonEmpty.length === 0 && field.type === 'dropdown') {
      add({
        code: 'field_has_no_values',
        severity: 'warning',
        target: { kind: 'field', fieldId: field.id },
        message: i18n.t('knowledge:taxonomy.validateSchema.dropdownHasNoOptionsSo'),
        hint: i18n.t('knowledge:taxonomy.validateSchema.addValuesBindColumnShared'),
      });
    }

    const codeSeen = new Map<string, number>();
    const valueSeen = new Map<string, number>();

    rows.forEach((row, index) => {
      const value = row.value.trim();
      const code = row.code.trim();
      // A wholly blank row is the grid's spare line, not a problem.
      if (!value && !code) return;

      if (value && !code) {
        add({
          code: 'missing_code',
          severity: 'error',
          target: { kind: 'cell', fieldId: field.id, row: index, column: 'code' },
          message: i18n.t('knowledge:taxonomy.validateSchema.valueHasNoCode', { value }),
          // This is the part people get wrong: it does not skip the segment.
          hint: i18n.t('knowledge:taxonomy.validateSchema.generatingNameFallsBackDisplay'),
        });
      }

      if (code && !value) {
        add({
          code: 'missing_value',
          severity: 'error',
          target: { kind: 'cell', fieldId: field.id, row: index, column: 'value' },
          message: i18n.t('knowledge:taxonomy.validateSchema.codeHasNoValue', { code }),
          hint: i18n.t('knowledge:taxonomy.validateSchema.nothingCanSelectRowGive'),
        });
      }

      if (code) {
        const key = duplicateKey(code);
        const first = codeSeen.get(key);
        if (first !== undefined) {
          add({
            code: 'duplicate_code',
            severity: 'error',
            target: { kind: 'cell', fieldId: field.id, row: index, column: 'code' },
            message: i18n.t('knowledge:taxonomy.validateSchema.codeAlreadyUsed', { code }),
            hint: i18n.t('knowledge:taxonomy.validateSchema.twoValuesSharingCodeMake2'),
          });
        } else {
          codeSeen.set(key, index);
        }

        for (const delim of delimiters) {
          if (code.includes(delim)) {
            add({
              code: 'code_contains_delimiter',
              severity: 'error',
              target: { kind: 'cell', fieldId: field.id, row: index, column: 'code' },
              message: i18n.t('knowledge:taxonomy.validateSchema.codeHasDelimiter', {
                code,
                delimiter: delim,
              }),
              hint: i18n.t('knowledge:taxonomy.validateSchema.changeCodeOrSeparatorSettings'),
            });
            break;
          }
        }

        if (/\s/.test(code)) {
          add({
            code: 'code_contains_space',
            severity: 'error',
            target: { kind: 'cell', fieldId: field.id, row: index, column: 'code' },
            message: i18n.t('knowledge:taxonomy.validateSchema.codeHasSpace', { code }),
            hint: i18n.t('knowledge:taxonomy.validateSchema.inlineCodeCannotHoldSpace'),
          });
        }
      }

      if (value) {
        const key = value.toLowerCase();
        if (valueSeen.has(key)) {
          add({
            code: 'duplicate_value',
            severity: 'warning',
            target: { kind: 'cell', fieldId: field.id, row: index, column: 'value' },
            message: i18n.t('knowledge:taxonomy.validateSchema.duplicateValue', { value }),
            hint: i18n.t(
              'knowledge:taxonomy.validateSchema.duplicateOptionsIndistinguishablePicker',
            ),
          });
        } else {
          valueSeen.set(key, index);
        }

        // Over-length is the pattern check REFUSING to test (its ReDoS guard),
        // not a mismatch. Reporting it as one names a cause that isn't true —
        // and contradicts the tooltip the same cell shows.
        if (pattern && value.length > MAX_TESTABLE_VALUE_LENGTH) {
          add({
            code: 'value_too_long',
            severity: 'error',
            target: { kind: 'cell', fieldId: field.id, row: index, column: 'value' },
            message: i18n.t('knowledge:taxonomy.validateSchema.valueOverCap', {
              length: value.length,
              cap: MAX_TESTABLE_VALUE_LENGTH,
            }),
            hint: i18n.t('knowledge:taxonomy.validateSchema.refusedBeforeAnyPatternApplied'),
          });
        } else if (pattern && !matchesFieldPattern(value, pattern)) {
          // The schema contradicting itself: it offers a value its own pattern
          // would reject at generation time.
          add({
            code: 'value_breaks_pattern',
            severity: 'error',
            target: { kind: 'cell', fieldId: field.id, row: index, column: 'value' },
            message: i18n.t('knowledge:taxonomy.validateSchema.valueFailsPattern', { value }),
            hint: i18n.t('knowledge:taxonomy.validateSchema.generatingNameValueWillRejected'),
          });
        }
      }
    });
  }

  // ── Rules ───────────────────────────────────────────────────────────────
  rules.forEach((rule, ruleIndex) => {
    for (const branch of getRuleBranches(rule)) {
      // The THEN side references values too — `limit_options` restricts the
      // target to a list of labels and `set_default` preselects one. Walking
      // only the IF side let a branch restrict a column to an option that had
      // been deleted, which is exactly what rule_dead_value exists to catch.
      const referenced = [
        ...flattenConditions(branch.when).map((c) => ({ ...c, role: 'condition' as const })),
        // eslint-disable-next-line i18next/no-literal-string -- discriminant, not copy
        { fieldId: branch.targetFieldId, value: '', role: 'target' as const },
        ...(branch.values ?? []).map((value) => ({
          fieldId: branch.targetFieldId,
          value,

          role: 'action' as const,
        })),
        ...(branch.defaultValue
          ? // eslint-disable-next-line i18next/no-literal-string -- discriminant, not copy
            [{ fieldId: branch.targetFieldId, value: branch.defaultValue, role: 'action' as const }]
          : []),
      ];

      for (const ref of referenced) {
        if (!ref.fieldId) continue;
        const field = byId.get(ref.fieldId);
        if (!field) {
          add({
            id: `rule_dead_field:${rule.id}:${branch.id}:${ref.fieldId}`,
            code: 'rule_dead_field',
            severity: 'error',
            target: { kind: 'schema' },
            message: i18n.t('knowledge:taxonomy.validateSchema.ruleDeadField', {
              index: ruleIndex + 1,
            }),
            hint: i18n.t('knowledge:taxonomy.validateSchema.openRulesTabRepointRemove'),
          });
          continue;
        }
        if (ref.role === 'target' || !ref.value) continue;
        // Its vocabulary is still being fetched, so "not an option" would mean
        // "not loaded yet" — every condition on a bound column would misfire.
        const boundVocab = field.listRef?.listId ? resolvedLists[field.id] : undefined;
        if (boundVocab?.pending) continue;

        /*
         * An UNGOVERNED field cannot hold a dead value.
         *
         * A `set_default` on a free-text, number or date column carries a typed
         * default rather than a pick from a list, so comparing it against a
         * vocabulary that does not exist reported every such rule as broken.
         *
         * Keyed on whether the field is GOVERNED, not on whether its vocabulary
         * came back empty. An earlier version skipped whenever `effectiveValues`
         * was empty and claimed `field_has_no_values` covered the rest — it does
         * not: that check sits behind `ownsItsValues`, false for any bound field.
         * So emptying a shared List silently cleared every `rule_dead_value` on
         * every schema referencing it, across all roles including `limit_options`
         * and IF-side conditions.
         */
        if (!isGoverned(field)) continue;
        /*
         * A binding whose fetch FAILED has no vocabulary to measure against —
         * its empty `values` mean "not fetched", exactly as a pending one's do.
         * The failure already reports as `broken_list_binding`, so measuring
         * anyway adds a second and wronger error for one cause: every value in
         * every rule touching that field reads as dead until the list loads.
         */
        if (boundVocab?.error) continue;
        const vocabulary = boundVocab?.values ?? field.values;

        const known = vocabulary.some(
          (v) => v.value.trim().toLowerCase() === ref.value.trim().toLowerCase(),
        );
        if (!known) {
          add({
            id: `rule_dead_value:${rule.id}:${branch.id}:${ref.role}:${ref.fieldId}:${ref.value}`,
            code: 'rule_dead_value',
            severity: 'warning',
            target: { kind: 'field', fieldId: field.id },
            message:
              ref.role === 'action'
                ? i18n.t('knowledge:taxonomy.validateSchema.ruleSetsMissingValue', {
                    rule: ruleIndex + 1,
                    value: ref.value,
                    field: field.name,
                  })
                : i18n.t('knowledge:taxonomy.validateSchema.ruleTestsMissingValue', {
                    rule: ruleIndex + 1,
                    value: ref.value,
                    field: field.name,
                  }),
            hint:
              ref.role === 'action'
                ? i18n.t('knowledge:taxonomy.validateSchema.branchWillOfferValueNothing')
                : i18n.t('knowledge:taxonomy.validateSchema.branchCanNeverFire'),
          });
        }
      }
    }
  });

  /**
   * The schema-level tracking code. Its pattern was previously unvalidated
   * ENTIRELY — a dangling reference resolved to an empty string at mint with no
   * error anywhere, so this is the only place it can surface.
   *
   * An EMPTY pattern is deliberately not reported: there is no opt-in signal
   * distinguishing "configured then emptied" from "never used", so flagging it
   * would fire on every schema that simply does not mint tracking codes.
   */
  checkPatternParts(settings?.trackingCode?.pattern, { kind: 'schema' }, 'tracking_code');

  // ── Outputs ─────────────────────────────────────────────────────────────
  for (const output of outputs ?? []) {
    // The date-mask half is shared with the tracking code; the dead-field half
    // stays local because its message names the output the user has to open.
    // eslint-disable-next-line i18next-templates/no-literal-string -- problem id prefix, not copy
    checkPatternParts(output.parts, { kind: 'schema' }, `output:${output.id}`, {
      deadField: false,
    });

    // Same reason as the rule tree: `parts` is jsonb, so trust the shape only
    // after checking it.
    if (!Array.isArray(output.parts)) continue;
    for (const part of output.parts) {
      if (part.kind === 'field' && part.fieldId && !byId.has(part.fieldId)) {
        add({
          id: `output_dead_field:${output.id}:${part.fieldId}`,
          code: 'output_dead_field',
          severity: 'error',
          target: { kind: 'schema' },
          message: i18n.t('knowledge:taxonomy.validateSchema.outputMissingColumn', {
            name: output.name,
          }),
          hint: i18n.t('knowledge:taxonomy.validateSchema.editOutputDropReplaceMissing'),
        });
        break;
      }
    }
  }

  // ── Character budget ────────────────────────────────────────────────────
  // Measured against the fields that actually reach the name, which is what
  // `channelStatuses` already does for the counter in the preview.
  //
  // The list vocabularies have to be materialized FIRST or the two disagree:
  // The estimator reads `field.values`, which for a bound column is the stale
  // inline fallback rather than the codes the name will really carry. Shared
  // with the counter: the two used to substitute on DIFFERENT conditions, so on
  // a cold cache the bar and the counter above it measured different arrays.
  const measuredFields = substituteResolvedValues(fields, resolvedLists);
  if (measuredFields.some(isFieldInName)) {
    for (const status of channelStatuses(measuredFields, settings, platforms)) {
      if (!status.over) continue;
      add({
        id: `over_channel_limit:${status.platform}`,
        code: 'over_channel_limit',
        severity: 'error',
        target: { kind: 'schema' },
        message: i18n.t('knowledge:taxonomy.validateSchema.longestNameOverLimit', {
          length: status.length,
          overBy: status.overBy,
          platform: status.platform,
          limit: status.limit,
        }),
        hint: i18n.t('knowledge:taxonomy.validateSchema.shortenCodesDateFormatsRaise'),
      });
    }
  }

  const ordered = [
    ...problems.filter((p) => p.severity === 'error'),
    ...problems.filter((p) => p.severity === 'warning'),
  ];

  return {
    problems: ordered,
    errorCount: ordered.filter((p) => p.severity === 'error').length,
    warningCount: ordered.filter((p) => p.severity === 'warning').length,
  };
}

/**
 * A key that survives re-running the check while it is still the same problem.
 * Built from the TARGET rather than from the message, so rewording the copy does
 * not change it.
 */
function problemId(code: SchemaProblemCode, target: SchemaProblemTarget): string {
  switch (target.kind) {
    case 'cell':
      return `${code}:${target.fieldId}:${target.row}:${target.column}`;
    case 'field':
      return `${code}:${target.fieldId}`;
    default:
      return code;
  }
}

function dedupe(values: string[]): string[] {
  return Array.from(new Set(values));
}

/** Index problems by cell, for the grid's per-cell lookup. */
export function indexProblemsByCell(problems: SchemaProblem[]): Map<string, SchemaProblem[]> {
  return indexProblems(problems).byCell;
}

/** Index problems by field, for the column header's indicator. */
export function indexProblemsByField(problems: SchemaProblem[]): Map<string, SchemaProblem[]> {
  return indexProblems(problems).byField;
}

export function cellKey(fieldId: string, row: number, column: 'value' | 'code'): string {
  return `${fieldId}:${row}:${column}`;
}

/** The three views of a report, in one pass. */
export interface ProblemIndex {
  /** Keyed by {@link cellKey}, for the grid's per-cell lookup. */
  byCell: Map<string, SchemaProblem[]>;
  /** Keyed by field id, for the column header's indicator. */
  byField: Map<string, SchemaProblem[]>;
  /** No anchor in the grid — reported in the summary or not at all. */
  schema: SchemaProblem[];
}

/**
 * Partitions a report by target kind.
 *
 * One function rather than three so the views cannot drift apart: the schema
 * partition used to be filtered by hand at the call site, which is how it ended
 * up as the only one of the three that was neither memoized nor tested.
 */
export function indexProblems(problems: SchemaProblem[]): ProblemIndex {
  const index: ProblemIndex = { byCell: new Map(), byField: new Map(), schema: [] };
  for (const p of problems) {
    if (p.target.kind === 'cell') {
      push(index.byCell, cellKey(p.target.fieldId, p.target.row, p.target.column), p);
    } else if (p.target.kind === 'field') {
      push(index.byField, p.target.fieldId, p);
    } else {
      index.schema.push(p);
    }
  }
  return index;
}

function push(map: Map<string, SchemaProblem[]>, key: string, problem: SchemaProblem): void {
  const bucket = map.get(key);
  if (bucket) bucket.push(problem);
  else map.set(key, [problem]);
}
